import { action, flow, register, SnapshotIn, types } from "@gadgetinc/mobx-quick-tree";
import { debounce } from "lodash";
import { comparer, reaction } from "mobx";
import {
  BillingAccount,
  BillingAccountProps,
  BillingPlan,
  BillingPlanProps,
  PaymentMethod,
  PaymentMethodProps,
  SpendLimitDetailsProps,
  UpcomingInvoice,
  UpcomingInvoiceProps,
} from "state-trees/src/Billing";
import { BillingContract } from "state-trees/src/BillingContract";
import { ServerContractStatusCode } from "state-trees/src/ServerContract";
import { GadgetDomainModelClass, GadgetModelClass } from "state-trees/src/utils";
import { billingApiRoute } from "../../components/auth/Billing";
import type { BillingPageProps } from "../../components/auth/Billing/BillingContext";
import { safelyRunAsyncEventHandler } from "../../lib/utils";

@register
export class StaffMasquerade extends GadgetDomainModelClass("StaffMasquerade", {
  billingAccountId: types.string,
}) {}

@register
export class SpendLimitThreshold extends GadgetModelClass("SpendLimitThreshold", {
  value: types.number,
}) {
  get isValidValue() {
    return this.value >= 0;
  }

  @action
  setValue(value: number) {
    this.value = value;
  }
}

@register
export class BillingSession extends GadgetDomainModelClass("BillingSession", {
  billingAccount: BillingAccount,
  billingPlan: BillingPlan,
  billingPlans: types.array(BillingPlan),
  paymentMethod: types.maybe(PaymentMethod),
  upcomingInvoice: UpcomingInvoice,
  addPaymentMethodModalOpen: types.optional(types.boolean, false),
  staffMasquerade: types.maybeNull(StaffMasquerade),
  polling: types.optional(types.boolean, false),
  maxSpend: types.maybeNull(types.number),
  thresholds: types.map(SpendLimitThreshold),
}) {
  afterCreate() {
    // Listen for threshold changes and debounce the update to the remote spend limit
    const debouncedUpdateRemoteSpendLimit = debounce(this.updateRemoteSpendLimit, 2000);
    reaction(
      () => this.thresholdsAsArray.filter((t) => t.isValidValue).map((t) => t.value),
      () => {
        safelyRunAsyncEventHandler(async () => {
          await debouncedUpdateRemoteSpendLimit();
        });
      },
      {
        equals: comparer.structural,
      }
    );
  }

  get billingOperationInProgress() {
    return (
      this.billingAccount.billingContract.status.code === ServerContractStatusCode.Pending ||
      this.billingAccount.billingContract.status.code === ServerContractStatusCode.NotYetStarted
    );
  }

  get pendingBankConfirmation() {
    return this.billingAccount.billingContract.pendingBankConfirmation;
  }

  get thresholdsAsArray() {
    return Array.from(this.thresholds.values());
  }

  @action
  setAddPaymentMethodModalOpen(open: boolean) {
    this.addPaymentMethodModalOpen = open;
  }

  @action
  setPaymentMethod(paymentMethod: PaymentMethodProps) {
    this.paymentMethod = PaymentMethod.create(paymentMethod);
  }

  @action
  setBillingAccount(billingAccount: BillingAccountProps) {
    this.billingAccount = BillingAccount.create(billingAccount);
  }

  @action
  setBillingAccountEmail(email: string) {
    this.billingAccount.setEmail(email);
  }

  @action
  setBillingPlan(billingPlan: BillingPlanProps) {
    this.billingPlan = BillingPlan.create(billingPlan);
  }

  @action
  setBillingPlans(billingPlans: BillingPlanProps[]) {
    this.billingPlans.replace(billingPlans.map((billingPlan) => BillingPlan.create(billingPlan)));
  }

  @action
  setUpcomingInvoice(upcomingInvoice: UpcomingInvoiceProps) {
    this.upcomingInvoice = UpcomingInvoice.create(upcomingInvoice);
  }

  @action
  setSpendLimit(details: SpendLimitDetailsProps) {
    this.maxSpend = details.enabled ? details.maxSpend : null;
    this.thresholds.clear();

    if (details.enabled) {
      for (const threshold of details.thresholds) {
        this.thresholds.put({ value: threshold });
      }
    }
  }

  @action
  setMaxSpend(maxSpend: number | null) {
    this.maxSpend = maxSpend;
  }

  @action
  addThreshold(value: number) {
    const existingThreshold = this.thresholdsAsArray.find((t) => t.value === value);
    if (existingThreshold) {
      return;
    }
    this.thresholds.put({
      value,
    });
  }

  @action
  removeThreshold(thresholdKey: string) {
    this.thresholds.delete(thresholdKey);
  }

  /**
   * Sends the local spend limit data to the server.
   */
  @action
  updateRemoteSpendLimit = flow(function* (this: BillingSession) {
    const path = billingApiRoute("/auth/api/billing/spend-limit", { staffMasquerade: this.staffMasquerade });
    const response = yield fetch(path, {
      method: "POST",
      body: JSON.stringify({
        thresholds: this.thresholdsAsArray.filter((t) => t.isValidValue).map((t) => t.value),
        maxSpend: this.maxSpend,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to update remote spend limit: ${response.statusText}`);
    }
  });

  @action
  refreshBillingState = flow(function* (this: BillingSession) {
    const path = billingApiRoute("/auth/api/billing", { staffMasquerade: this.staffMasquerade });
    const response = yield fetch(path);

    if (!response.ok) {
      throw new Error(`Failed to refresh billing state: ${response.statusText}`);
    }

    const data: BillingPageProps = yield response.json();

    this.setBillingAccount(data.billingAccount);
    this.setBillingPlan(data.billingPlan);
    this.setBillingPlans(data.billingPlans);
    this.setUpcomingInvoice(data.upcomingInvoice);
    if (data.paymentMethod) {
      this.setPaymentMethod(data.paymentMethod);
    }
    if (data.spendLimitDetails) {
      this.setSpendLimit(data.spendLimitDetails);
    }
  });

  @action
  refreshBillingContract = flow(function* (this: BillingSession) {
    const path = billingApiRoute("/auth/api/billing/contract", { staffMasquerade: this.staffMasquerade });
    const response = yield fetch(path);

    if (!response.ok) {
      throw new Error(`Failed to refresh billing contract: ${response.statusText}`);
    }

    const billingContract: SnapshotIn<typeof BillingContract> = yield response.json();

    this.billingAccount.setBillingContract(billingContract);
  });

  @action
  waitForBillingOperationToSettle = flow(function* (this: BillingSession) {
    if (this.polling) {
      return;
    }

    this.polling = true;

    try {
      yield this.refreshBillingContract();

      while (this.billingOperationInProgress || this.billingAccount.billingContract.hasPendingPayments) {
        let delay = this.billingAccount.billingContract.hasPendingPayments ? 180000 : 3000;
        if (typeof window !== "undefined" && (window as any)._GADGET_TEST_BILLING_POLL_DELAY) {
          delay = (window as any)._GADGET_TEST_BILLING_POLL_DELAY;
        }
        yield new Promise((resolve) => setTimeout(resolve, delay));
        yield this.refreshBillingContract();

        if (this.billingAccount.billingContract.pendingBankConfirmation) {
          yield this.refreshBillingState();
        }
      }
    } finally {
      this.polling = false;
    }
  });
}
